import { useState } from 'react';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../theme/tokens';
import { useAuth } from '../../lib/auth';
import { useNotify } from '../../lib/notify';
import {
  useCreateTalentComment,
  useDeleteTalentComment,
  useTalentComments,
  useUpdateTalentComment,
} from './useTalentComments';
import type { CommentItem } from './talentCommentsApi';

const MAX_TEXT_LENGTH = 2000;

/**
 * §4.5 — Comentarios del cliente sobre un talent. Append + edit by author + soft delete.
 *
 * Visible al author + colegas del mismo cliente + recruiter.
 *
 * Modo impersonation (§4.9): caja de creación se esconde, edit/delete de comentarios
 * existentes se esconden (botones no se renderizan). Solo se muestra el feed read-only.
 */
export function TalentCommentsPanel({
  shortlistPublicKey,
  talentPublicKey,
}: {
  shortlistPublicKey: string;
  talentPublicKey: string;
}) {
  const { t } = useTranslation();
  const auth = useAuth();
  const notify = useNotify();
  const [text, setText] = useState('');

  const query = useTalentComments({ shortlistPublicKey, talentPublicKey, pageSize: 50 });
  const createMutation = useCreateTalentComment({ shortlistPublicKey, talentPublicKey });

  const isImpersonating = false /* impersonation not implemented in monorepo */;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = text.trim();
    if (!trimmed || trimmed.length > MAX_TEXT_LENGTH || createMutation.isPending) return;
    try {
      await createMutation.mutateAsync({ text: trimmed });
      setText('');
    } catch (err) {
      // SW-137: surface el status code para debugging (CSRF 403 vs auth 401 vs val 400).
      // El error toast queda con el copy genérico, pero el detail va al console + queda
      // accesible via dev tools.
      const status =
        err && typeof err === 'object' && 'status' in err ? (err as { status: number }).status : 0;
      if (typeof console !== 'undefined') {
        console.error('[TalentCommentsPanel] createComment failed', { status, err });
      }
      notify.error(`${t('opportunities.talent.comments.error')} (${status || 'network'})`);
    }
  }

  return (
    <Box
      component="section"
      data-testid="talent-comments-panel"
      sx={{
        padding: '20px 24px',
        background: tokens.color.surface,
        border: `1px solid ${tokens.color.line}`,
        borderRadius: '16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '14px',
      }}
    >
      <Typography
        sx={{
          fontFamily: tokens.font.mono,
          fontSize: 11,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: tokens.color.muted,
        }}
      >
        {t('opportunities.talent.comments.sectionTitle')}
      </Typography>

      {!isImpersonating && (
        <Box component="form" onSubmit={handleSubmit} data-testid="talent-comments-form">
          <Box
            component="textarea"
            data-testid="talent-comments-input"
            placeholder={t('opportunities.talent.comments.placeholder')}
            maxLength={MAX_TEXT_LENGTH}
            value={text}
            onChange={(e) => setText(e.currentTarget.value)}
            disabled={createMutation.isPending}
            sx={{
              width: '100%',
              minHeight: 70,
              padding: '10px 12px',
              border: `1px solid ${tokens.color.line}`,
              borderRadius: '10px',
              fontFamily: 'inherit',
              fontSize: 13,
              resize: 'vertical',
              background: tokens.color.surface2,
              color: tokens.color.ink,
              '&:focus': { outline: `2px solid ${tokens.color.accent}` },
            }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: '8px', gap: '10px', alignItems: 'center' }}>
            <Typography sx={{ fontSize: 11, color: tokens.color.muted }}>
              {text.length} / {MAX_TEXT_LENGTH}
            </Typography>
            <Box
              component="button"
              type="submit"
              disabled={createMutation.isPending || text.trim().length === 0}
              data-testid="talent-comments-submit"
              sx={{
                padding: '8px 14px',
                borderRadius: '10px',
                background: tokens.color.accent,
                color: tokens.color.accentInk,
                fontFamily: 'inherit',
                fontSize: 13,
                fontWeight: 600,
                border: 0,
                cursor: createMutation.isPending ? 'wait' : 'pointer',
                opacity: createMutation.isPending ? 0.7 : 1,
                '&:hover': { opacity: createMutation.isPending ? 0.7 : 0.92 },
                '&:disabled': { opacity: 0.4, cursor: 'not-allowed' },
              }}
            >
              {createMutation.isPending
                ? t('opportunities.talent.comments.posting')
                : t('opportunities.talent.comments.post')}
            </Box>
          </Box>
        </Box>
      )}

      {query.isPending && <CircularProgress size={16} />}
      {query.isError && (
        <Alert severity="error">{t('opportunities.talent.comments.loadError')}</Alert>
      )}
      {query.isSuccess && query.data.items.length === 0 && (
        <Typography sx={{ fontSize: 13, color: tokens.color.muted }}>
          {t('opportunities.talent.comments.empty')}
        </Typography>
      )}
      {query.isSuccess && query.data.items.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }} data-testid="talent-comments-list">
          {query.data.items.map((c) => (
            <CommentRow
              key={c.id}
              comment={c}
              currentUserSub={auth.user?.sub ?? null}
              isImpersonating={isImpersonating}
              shortlistPublicKey={shortlistPublicKey}
              talentPublicKey={talentPublicKey}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}

function CommentRow({
  comment,
  currentUserSub,
  isImpersonating,
  shortlistPublicKey,
  talentPublicKey,
}: {
  comment: CommentItem;
  currentUserSub: string | null;
  isImpersonating: boolean;
  shortlistPublicKey: string;
  talentPublicKey: string;
}) {
  const { t } = useTranslation();
  const notify = useNotify();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(comment.text);

  const updateMutation = useUpdateTalentComment({ shortlistPublicKey, talentPublicKey });
  const deleteMutation = useDeleteTalentComment({ shortlistPublicKey, talentPublicKey });

  const isDeleted = comment.deletedAt !== null;
  const isEdited = comment.updatedOn !== null && !isDeleted;

  // El currentUserSub viene del JWT. Para auth normal es el ClientPortalUser.Key (Guid).
  // Para impersonation es "impersonation-{rid}-{cid}". El BE valida por
  // ClientPortalUserId === comment.ClientPortalUserId, así que para detectar "este es
  // mi comment" comparamos sub del JWT con un sub virtual derivable del comment.
  // Como esa info no está en el wire shape del comment, una heurística: el comment es
  // mío si el authorEmail coincide con algo conocido. Sin acceso directo al email del
  // current user (no expuesto en /auth/me) usamos un fallback conservador: solo
  // permitir edit/delete si el FE puede verificar por server (BE retorna 403/404 si
  // intenta editar uno que no es suyo). El UX: mostrar siempre los botones para usuarios
  // no-impersonation; el BE rechaza si no es author.
  // (Mejora futura: BE incluye `authorSub` en el comment item.)
  const showAuthorActions = !isImpersonating && !isDeleted && currentUserSub !== null;

  async function handleSaveEdit() {
    const trimmed = draft.trim();
    if (!trimmed || trimmed.length > MAX_TEXT_LENGTH) return;
    try {
      await updateMutation.mutateAsync({ commentPublicKey: comment.id, text: trimmed });
      setEditing(false);
    } catch {
      notify.error(t('opportunities.talent.comments.editError'));
    }
  }

  async function handleDelete() {
    if (!window.confirm(t('opportunities.talent.comments.confirmDelete'))) return;
    try {
      await deleteMutation.mutateAsync(comment.id);
    } catch {
      notify.error(t('opportunities.talent.comments.deleteError'));
    }
  }

  return (
    <Box
      data-testid={`talent-comment-${comment.id}`}
      data-deleted={isDeleted ? 'true' : 'false'}
      sx={{
        padding: '12px 14px',
        background: tokens.color.surface2,
        borderRadius: '10px',
        border: `1px solid ${tokens.color.line}`,
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: '6px', gap: '12px' }}>
        <Typography sx={{ fontSize: 12, fontWeight: 600, color: tokens.color.ink }}>
          {comment.authorFullName ?? comment.authorEmail}
        </Typography>
        <Typography sx={{ fontSize: 11, color: tokens.color.muted }}>
          {new Date(comment.createdOn).toLocaleString()}
          {isEdited && ` · ${t('opportunities.talent.comments.edited')}`}
        </Typography>
      </Box>

      {isDeleted ? (
        <Typography
          data-testid={`talent-comment-deleted-${comment.id}`}
          sx={{ fontSize: 13, fontStyle: 'italic', color: tokens.color.muted }}
        >
          {t('opportunities.talent.comments.deletedPlaceholder')}
        </Typography>
      ) : editing ? (
        <Box>
          <Box
            component="textarea"
            data-testid={`talent-comment-edit-${comment.id}`}
            maxLength={MAX_TEXT_LENGTH}
            value={draft}
            onChange={(e) => setDraft(e.currentTarget.value)}
            disabled={updateMutation.isPending}
            sx={{
              width: '100%',
              minHeight: 60,
              padding: '8px 10px',
              border: `1px solid ${tokens.color.line}`,
              borderRadius: '8px',
              fontFamily: 'inherit',
              fontSize: 13,
              resize: 'vertical',
              background: tokens.color.surface,
              color: tokens.color.ink,
            }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', mt: '6px' }}>
            <Box
              component="button"
              type="button"
              onClick={() => { setEditing(false); setDraft(comment.text); }}
              disabled={updateMutation.isPending}
              sx={miniButtonSx(false)}
            >
              {t('opportunities.talent.comments.cancel')}
            </Box>
            <Box
              component="button"
              type="button"
              onClick={() => void handleSaveEdit()}
              disabled={updateMutation.isPending || draft.trim().length === 0}
              data-testid={`talent-comment-save-${comment.id}`}
              sx={miniButtonSx(true)}
            >
              {updateMutation.isPending
                ? t('opportunities.talent.comments.saving')
                : t('opportunities.talent.comments.save')}
            </Box>
          </Box>
        </Box>
      ) : (
        <Typography sx={{ fontSize: 13, color: tokens.color.ink, whiteSpace: 'pre-wrap' }}>
          {comment.text}
        </Typography>
      )}

      {showAuthorActions && !editing && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', mt: '8px' }}>
          <Box
            component="button"
            type="button"
            onClick={() => { setEditing(true); setDraft(comment.text); }}
            data-testid={`talent-comment-edit-button-${comment.id}`}
            sx={linkButtonSx}
          >
            {t('opportunities.talent.comments.edit')}
          </Box>
          <Box
            component="button"
            type="button"
            onClick={() => void handleDelete()}
            disabled={deleteMutation.isPending}
            data-testid={`talent-comment-delete-button-${comment.id}`}
            sx={linkButtonSx}
          >
            {deleteMutation.isPending
              ? t('opportunities.talent.comments.deleting')
              : t('opportunities.talent.comments.delete')}
          </Box>
        </Box>
      )}
    </Box>
  );
}

function miniButtonSx(primary: boolean) {
  return {
    padding: '5px 11px',
    borderRadius: '8px',
    fontFamily: 'inherit',
    fontSize: 12,
    fontWeight: 600,
    border: primary ? 0 : `1px solid ${tokens.color.line}`,
    background: primary ? tokens.color.accent : 'transparent',
    color: primary ? tokens.color.accentInk : tokens.color.ink,
    cursor: 'pointer',
    '&:disabled': { opacity: 0.5, cursor: 'not-allowed' },
  } as const;
}

const linkButtonSx = {
  background: 'transparent',
  border: 0,
  color: tokens.color.muted,
  fontFamily: 'inherit',
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
  padding: '2px 6px',
  '&:hover': { color: tokens.color.ink },
  '&:disabled': { opacity: 0.5, cursor: 'not-allowed' },
} as const;
